import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

import { getBackendUrl, getApiUrl } from '../../config/environment';
const BACKEND_URL = getBackendUrl();

/**
 * Admin Component for managing affiliate levels (Bronze, Silver, Gold, etc.)
 * CRUD operations for gamification system
 */
const AffiliateLevelsManagement = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    badge_icon: '🥉',
    min_referrals: 0,
    commission_rate: 10,
    message: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliate-levels/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLevels(data.levels);
      } else {
        toast.error('Erreur lors du chargement des niveaux');
      }
    } catch (error) {
      console.error('Error fetching levels:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const url = editingLevel
        ? `${BACKEND_URL}/api/admin/affiliate-levels/${editingLevel.id}`
        : `${BACKEND_URL}/api/admin/affiliate-levels/`;
      
      const method = editingLevel ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingLevel ? 'Niveau modifié !' : 'Niveau créé !');
        setShowModal(false);
        setEditingLevel(null);
        resetForm();
        fetchLevels();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (levelId, levelName) => {
    if (!window.confirm(`Supprimer le niveau ${levelName} ?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliate-levels/${levelId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Niveau supprimé');
        fetchLevels();
      } else {
        toast.error('Erreur de suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      badge_icon: '🥉',
      min_referrals: 0,
      commission_rate: 10,
      message: '',
      order: 0,
      active: true
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingLevel(null);
    setShowModal(true);
  };

  const openEditModal = (level) => {
    setFormData({
      name: level.name,
      badge_icon: level.badge_icon,
      min_referrals: level.min_referrals,
      commission_rate: level.commission_rate,
      message: level.message,
      order: level.order,
      active: level.active
    });
    setEditingLevel(level);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Niveaux d'Affiliation</h2>
          <p className="text-gray-600 text-sm">Gérer la gamification du programme affilié</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={20} />
          Créer un Niveau
        </button>
      </div>

      {/* Levels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {levels.map((level, index) => (
          <motion.div
            key={level.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`relative bg-white rounded-xl p-6 shadow-md border-2 ${
              level.active ? 'border-purple-200' : 'border-gray-200'
            }`}
          >
            {/* Active badge */}
            {level.active && (
              <div className="absolute top-3 right-3">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-semibold">
                  Actif
                </span>
              </div>
            )}

            {/* Badge icon */}
            <div className="text-center mb-4">
              <span className="text-5xl">{level.badge_icon}</span>
            </div>

            {/* Level info */}
            <h3 className="text-xl font-bold text-center text-gray-800 mb-2">{level.name}</h3>
            <div className="space-y-2 text-sm text-gray-600 mb-4">
              <div className="flex justify-between">
                <span>Seuil min:</span>
                <span className="font-semibold">{level.min_referrals} filleuls</span>
              </div>
              <div className="flex justify-between">
                <span>Commission:</span>
                <span className="font-semibold text-purple-600">{level.commission_rate}%</span>
              </div>
              <div className="flex justify-between">
                <span>Ordre:</span>
                <span className="font-semibold">{level.order}</span>
              </div>
            </div>

            {/* Message */}
            <p className="text-xs text-gray-500 italic mb-4 line-clamp-2">{level.message}</p>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => openEditModal(level)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Edit2 size={16} />
                Modifier
              </button>
              <button
                onClick={() => handleDelete(level.id, level.name)}
                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty state */}
      {levels.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Aucun niveau créé. Commencez par en créer un !</p>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingLevel ? 'Modifier le Niveau' : 'Créer un Niveau'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du niveau
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Bronze, Silver, Gold..."
                  />
                </div>

                {/* Badge Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge (emoji)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.badge_icon}
                    onChange={(e) => setFormData({...formData, badge_icon: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-2xl"
                    placeholder="🥉"
                    maxLength={10}
                  />
                </div>

                {/* Min Referrals */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seuil minimum (filleuls payants)
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.min_referrals}
                    onChange={(e) => setFormData({...formData, min_referrals: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Commission Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taux de commission (%)
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.commission_rate}
                    onChange={(e) => setFormData({...formData, commission_rate: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Commission par vente : {(22.90 * formData.commission_rate / 100).toFixed(2)}€
                  </p>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message motivationnel
                  </label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="3"
                    placeholder="Bravo ! Tu construis ta base !"
                  />
                </div>

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ordre (0 = niveau le plus bas)
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* Active */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Niveau actif
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Save size={18} />
                    {editingLevel ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AffiliateLevelsManagement;
